import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import '../styles/CalendarView.css';
import { useModal } from '../context/ModalContext';
import MeetingScheduler from './MeetingScheduler';

const localizer = momentLocalizer(moment);

const CalendarView = ({ user, activeSection, noWrapper = false }) => {
  const modal = useModal();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    summary: '',
    description: '',
    location: '',
    startTime: '',
    endTime: ''
  });
  // Add state to track the current date view
  const [currentDate, setCurrentDate] = useState(new Date());
  // Add state for meeting scheduler view (not modal)
  const [showMeetingScheduler, setShowMeetingScheduler] = useState(false);

  // Get calendar provider name
  const calendarProvider = user?.auth_provider === 'Microsoft' ? 'Microsoft Calendar' : 'Google Calendar';

  // Helper function to parse dates correctly, avoiding timezone issues
  const parseEventDate = (dateStr) => {
    // Handle date-only strings (all-day events) to avoid timezone conversion issues
    if (typeof dateStr === 'string' && dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day, 12, 0, 0); // Set to noon local time
    }
    return new Date(dateStr);
  };

  // Guards against overlapping requests (double effect fires, refresh-spam)
  const isFetchingRef = useRef(false);

  // Fetch events from your backend API with explicit time range
  const fetchEvents = useCallback(async (startDate = null, endDate = null) => {
    if (isFetchingRef.current) {
      return;
    }
    isFetchingRef.current = true;
    try {
      setIsLoading(true);

      // If no dates provided, fetch current month + next 2 months (e.g., Dec, Jan, Feb)
      const now = new Date();
      const start = startDate || new Date(now.getFullYear(), now.getMonth(), 1);
      const end = endDate || new Date(now.getFullYear(), now.getMonth() + 3, 0);

      // Build query parameters
      const params = new URLSearchParams({
        timeMin: start.toISOString(),
        timeMax: end.toISOString(),
        maxResults: 2500 // Increased limit to ensure all events are fetched
      });
      
      const response = await fetch(`/api/calendar/events?${params}`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (!response.ok) {
        // Check if user doesn't have calendar access
        if (data.no_calendar_access) {
          setError('no_calendar_access');
          return;
        }
        // Check if user needs to re-authenticate
        if (data.needs_reauth) {
          setError('calendar_reauth_needed');
          return;
        }
        throw new Error(data.message || 'Failed to fetch calendar events');
      }

      if (data.events) {
        // Transform calendar events to react-big-calendar format
        // Filter out working location events and other non-event items
        const transformedEvents = data.events
          .filter(event => {
            // Skip events that are just working locations
            if (event.eventType === 'workingLocation' || event.eventType === 'outOfOffice') {
              return false;
            }
            // Skip events with no summary that aren't real events
            if (!event.summary && event.eventType !== 'default' && !event.start) {
              return false;
            }
            return true;
          })
          .map(event => ({
            id: event.id,
            title: event.summary || 'Untitled Event',
            start: parseEventDate(event.start.dateTime || event.start.date),
            end: parseEventDate(event.end.dateTime || event.end.date),
            description: event.description || '',
            location: event.location || '',
            htmlLink: event.htmlLink,
            allDay: !event.start.dateTime, // all-day events don't have dateTime
            // Add a visual indicator for past events
            isPast: new Date(event.start.dateTime || event.start.date) < new Date(),
          }));

        setEvents(transformedEvents);
      }
      setError(null);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  }, []);

  useEffect(() => {
    // Scheduler subsection doesn't display fetched events - skip the call
    if (activeSection === 'calendar-scheduler') {
      setIsLoading(false);
      return;
    }
    fetchEvents();
  }, [fetchEvents, activeSection]);

  // Handle navigation - simply update the current date
  const handleNavigate = useCallback((date) => {
    setCurrentDate(date);
  }, []);

  // Handle slot selection (when user clicks on calendar to create event)
  const handleSelectSlot = useCallback((slotInfo) => {
    setSelectedSlot(slotInfo);
    setSelectedEvent(null);
    setEventForm({
      summary: '',
      description: '',
      location: '',
      startTime: moment(slotInfo.start).format('YYYY-MM-DDTHH:mm'),
      endTime: moment(slotInfo.end).format('YYYY-MM-DDTHH:mm')
    });
    setShowEventModal(true);
  }, []);

  // Handle event selection (when user clicks on existing event)
  const handleSelectEvent = useCallback((event) => {
    setSelectedEvent(event);
    setSelectedSlot(null);
    setEventForm({
      summary: event.title,
      description: event.description || '',
      location: event.location || '',
      startTime: moment(event.start).format('YYYY-MM-DDTHH:mm'),
      endTime: moment(event.end).format('YYYY-MM-DDTHH:mm')
    });
    setShowEventModal(true);
  }, []);

  // Create new event
  const handleCreateEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        summary: eventForm.summary,
        description: eventForm.description,
        location: eventForm.location,
        start: {
          dateTime: new Date(eventForm.startTime).toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          dateTime: new Date(eventForm.endTime).toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      const response = await fetch('/api/calendar/events', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        throw new Error('Failed to create event');
      }

      // Refresh events after creating
      await fetchEvents();
      setShowEventModal(false);
      resetForm();
    } catch (err) {
      console.error('Error creating event:', err);
      modal.error('Failed to create event: ' + err.message);
    }
  };

  // Update existing event
  const handleUpdateEvent = async (e) => {
    e.preventDefault();
    try {
      const eventData = {
        summary: eventForm.summary,
        description: eventForm.description,
        location: eventForm.location,
        start: {
          dateTime: new Date(eventForm.startTime).toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
        end: {
          dateTime: new Date(eventForm.endTime).toISOString(),
          timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      };

      const response = await fetch(`/api/calendar/events/${selectedEvent.id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventData)
      });

      if (!response.ok) {
        throw new Error('Failed to update event');
      }

      await fetchEvents();
      setShowEventModal(false);
      resetForm();
    } catch (err) {
      console.error('Error updating event:', err);
      modal.error('Failed to update event: ' + err.message);
    }
  };

  // Delete event
  const handleDeleteEvent = async () => {
    const result = await modal.confirm('Are you sure you want to delete this event?', 'Delete Event');
    if (!result) {
      return;
    }

    try {
      const response = await fetch(`/api/calendar/events/${selectedEvent.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete event');
      }

      await fetchEvents();
      setShowEventModal(false);
      resetForm();
    } catch (err) {
      console.error('Error deleting event:', err);
      modal.error('Failed to delete event: ' + err.message);
    }
  };

  const resetForm = () => {
    setEventForm({
      summary: '',
      description: '',
      location: '',
      startTime: '',
      endTime: ''
    });
    setSelectedEvent(null);
    setSelectedSlot(null);
  };

  const handleFormChange = (e) => {
    setEventForm({
      ...eventForm,
      [e.target.name]: e.target.value
    });
  };

  // Custom event style to visually differentiate past events
  const eventStyleGetter = (event, start, end, isSelected) => {
    let backgroundColor = '#1a73e8';
    let opacity = 1;
    
    if (event.isPast) {
      backgroundColor = '#9e9e9e';
      opacity = 0.8;
    }
    
    const style = {
      backgroundColor: backgroundColor,
      borderRadius: '4px',
      opacity: opacity,
      color: 'white',
      border: '0px',
      display: 'block',
      fontSize: '12px',
      padding: '2px 4px'
    };

    return {
      style: style
    };
  };

  if (error) {
    if (error === 'no_calendar_access') {
      return (
        <div className="calendar-error">
          <h3>No Access to {calendarProvider}</h3>
          <p>Your account does not have {calendarProvider} enabled.</p>
          <p>This may be because:</p>
          <ul style={{ textAlign: 'left', marginLeft: '2rem' }}>
            <li>Your organization has disabled {calendarProvider} for your account</li>
            <li>Your subscription doesn't include Calendar access</li>
            <li>Calendar access has been restricted by your admin</li>
          </ul>
          <p>Please contact your organization's administrator to enable {calendarProvider} access.</p>
        </div>
      );
    }

    if (error === 'calendar_reauth_needed') {
      return (
        <div className="calendar-error">
          <h3>Calendar Access Required</h3>
          <p>To use the calendar feature, you need to grant calendar access permissions.</p>
          <p>Please log out and log back in to authorize calendar access.</p>
          <button
            onClick={() => window.location.href = '/auth/logout'}
            className="retry-btn"
          >
            Log Out and Re-authenticate
          </button>
        </div>
      );
    }

    return (
      <div className="calendar-error">
        <h3>Unable to load calendar</h3>
        <p>{error}</p>
        <button onClick={() => fetchEvents()} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="calendar-loading">
        <div className="loading-spinner"></div>
        <p>Loading your {calendarProvider}...</p>
      </div>
    );
  }

  // Count past and future events for display
  const pastEventsCount = events.filter(e => e.isPast).length;
  const futureEventsCount = events.filter(e => !e.isPast).length;

  const renderCalendarContent = () => {
    if (activeSection === 'calendar-scheduler') {
      return (
        <MeetingScheduler
          user={user}
          onClose={() => {}}
          onMeetingCreated={(meeting) => {
            // Refresh calendar events to show the new meeting
            fetchEvents();
          }}
        />
      );
    }

    return (
      <div className="calendar-content">
        <div className="calendar-header">
          <div className="calendar-header-info">
            <h2>My Calendar</h2>
            <p>
              View and manage your {calendarProvider} - Showing {events.length} events
              ({pastEventsCount} past, {futureEventsCount} upcoming)
            </p>
          </div>
          <div className="calendar-actions">
            <button onClick={() => fetchEvents()} className="refresh-btn">
              Refresh Calendar
            </button>
            <a
              href={user?.auth_provider === 'Microsoft' ? 'https://outlook.office.com/calendar/' : 'https://calendar.google.com/calendar/u/0/r'}
              target="_blank"
              rel="noopener noreferrer"
              className="open-full-calendar-btn"
            >
              Open in {calendarProvider}
            </a>
          </div>
        </div>

        <div className="calendar-view-wrapper">
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            selectable
            onSelectSlot={handleSelectSlot}
            onSelectEvent={handleSelectEvent}
            views={['month']}
            defaultView="month"
            date={currentDate}
            onNavigate={handleNavigate}
            popup
            showMultiDayTimes={false}
            tooltipAccessor={(event) => event.description || event.title}
            eventPropGetter={eventStyleGetter}
            dayLayoutAlgorithm='no-overlap'
            step={60}
            timeslots={1}
            length={21}
            formats={{
              eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
                localizer.format(start, 'h:mma', culture) + ' - ' + localizer.format(end, 'h:mma', culture),
              timeGutterFormat: (date, culture, localizer) =>
                localizer.format(date, 'ha', culture),
              monthHeaderFormat: (date, culture, localizer) =>
                localizer.format(date, 'MMMM YYYY', culture),
            }}
          />
        </div>
      </div>
    );
  };

  const calendarContent = (
    <>
      {renderCalendarContent()}

      {showEventModal && (
        <div className="event-modal-overlay" onClick={() => setShowEventModal(false)}>
          <div className="event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="event-modal-header">
              <h3>{selectedEvent ? 'Edit Event' : 'Create New Event'}</h3>
              <button
                className="modal-close-btn"
                onClick={() => setShowEventModal(false)}
                aria-label="Close"
              />
            </div>

            <form onSubmit={selectedEvent ? handleUpdateEvent : handleCreateEvent}>
              <div className="form-group">
                <label htmlFor="summary">Event Title *</label>
                <input
                  type="text"
                  id="summary"
                  name="summary"
                  value={eventForm.summary}
                  onChange={handleFormChange}
                  required
                  placeholder="Meeting with team"
                />
              </div>

              <div className="form-group">
                <label htmlFor="startTime">Start Time *</label>
                <input
                  type="datetime-local"
                  id="startTime"
                  name="startTime"
                  value={eventForm.startTime}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="endTime">End Time *</label>
                <input
                  type="datetime-local"
                  id="endTime"
                  name="endTime"
                  value={eventForm.endTime}
                  onChange={handleFormChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="location">Location</label>
                <input
                  type="text"
                  id="location"
                  name="location"
                  value={eventForm.location}
                  onChange={handleFormChange}
                  placeholder="Conference Room A"
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  name="description"
                  value={eventForm.description}
                  onChange={handleFormChange}
                  rows="4"
                  placeholder="Add event details..."
                />
              </div>

              <div className="event-modal-actions">
                {selectedEvent && (
                  <button
                    type="button"
                    onClick={handleDeleteEvent}
                    className="delete-event-btn"
                  >
                    Delete Event
                  </button>
                )}
                <div className="right-actions">
                  <button
                    type="button"
                    onClick={() => setShowEventModal(false)}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="save-event-btn"
                  >
                    {selectedEvent ? 'Update Event' : 'Create Event'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );

  if (noWrapper) {
    return calendarContent;
  }

  return (
    <div className="super-admin-internal-layout">
      <div className="internal-content">
        {calendarContent}
      </div>
    </div>
  );
};

export default CalendarView;